import React, { useState } from 'react';
import { PrinterIcon } from '@heroicons/react/24/outline';
import AnnotatedImage from '../components/AnnotatedImage';
import { HELP_SECTIONS } from './helpContent';

function FieldList({ fields, heading = 'Form Fields' }) {
  if (!fields || fields.length === 0) return null;
  return (
    <div className="mb-6">
      <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">{heading}</h3>
      <div className="border border-gray-200 divide-y divide-gray-100">
        {fields.map((f, i) => (
          <div key={i} className="px-4 py-2.5 text-sm">
            <span className="font-medium text-gray-800">{f.label}</span>
            {f.hint && <p className="text-gray-500 text-xs mt-0.5">{f.hint}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}

function NoteList({ notes }) {
  if (!notes || notes.length === 0) return null;
  return (
    <div className="mb-6">
      <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Things to Know</h3>
      <ul className="space-y-2">
        {notes.map((n, i) => (
          <li key={i} className="flex gap-2 text-sm text-gray-600">
            <span className="text-accent shrink-0">•</span>
            <span>{n}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ScreenshotGroup({ screenshots, fallbackAlt }) {
  if (!screenshots || screenshots.length === 0) return null;
  const showLabels = screenshots.length > 1;
  return (
    <>
      {screenshots.map((shot, i) => (
        <div key={i} className="mb-6">
          {showLabels && shot.label && (
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">{shot.label}</h4>
          )}
          <AnnotatedImage src={shot.src} alt={shot.alt || fallbackAlt} annotations={shot.annotations} />
        </div>
      ))}
    </>
  );
}

function Section({ section, active }) {
  return (
    <section
      id={`help-${section.id}`}
      className={`${active ? 'block' : 'hidden'} print:block print:break-after-page`}
    >
      <h2 className="text-xl font-display font-bold text-primary-dark mb-1">{section.title}</h2>
      <p className="text-sm text-gray-500 mb-5">{section.overview}</p>

      <ScreenshotGroup screenshots={section.screenshots} fallbackAlt={section.title} />

      <FieldList fields={section.fields} heading={section.fieldsHeading} />
      <NoteList notes={section.notes} />

      {section.subsections?.map((sub, i) => (
        <div key={i} className="mt-6 pt-6 border-t border-gray-100">
          <h3 className="font-semibold text-primary mb-3">{sub.title}</h3>
          <ScreenshotGroup screenshots={sub.screenshots} fallbackAlt={sub.title} />
          <FieldList fields={sub.fields} />
          <NoteList notes={sub.notes} />
        </div>
      ))}
    </section>
  );
}

export default function AdminHelp() {
  const [activeId, setActiveId] = useState(HELP_SECTIONS[0].id);

  return (
    <div>
      <div className="flex items-center justify-between mb-6 print:hidden">
        <div>
          <h1 className="text-2xl font-display font-bold text-primary-dark">Help &amp; Guide</h1>
          <p className="text-sm text-gray-500 mt-1">A walkthrough of every section of this admin panel</p>
        </div>
        <button
          onClick={() => window.print()}
          className="btn-outline text-sm flex items-center gap-2"
        >
          <PrinterIcon className="w-4 h-4" /> Print / Save as PDF
        </button>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        <nav className="lg:w-56 shrink-0 print:hidden">
          <div className="lg:sticky lg:top-6 bg-white border border-gray-100 shadow-sm p-2 space-y-0.5">
            {HELP_SECTIONS.map(s => (
              <button
                key={s.id}
                onClick={() => setActiveId(s.id)}
                className={`w-full text-left px-3 py-2 text-sm transition-colors ${activeId === s.id
                  ? 'bg-accent text-white font-medium'
                  : 'text-gray-600 hover:bg-gray-50'}`}
              >
                {s.title}
              </button>
            ))}
          </div>
        </nav>

        <div className="flex-1 bg-white border border-gray-100 shadow-sm p-6 print:border-0 print:shadow-none print:p-0">
          {/* Printable cover — screen-hidden, shows once at the top of the printed/PDF version */}
          <div className="hidden print:block mb-8">
            <h1 className="text-2xl font-display font-bold text-primary-dark">Sankyo Machine Tools — Admin Panel Guide</h1>
            <p className="text-sm text-gray-500 mt-1">Printed {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
          </div>
          {HELP_SECTIONS.map(s => (
            <Section key={s.id} section={s} active={activeId === s.id} />
          ))}
        </div>
      </div>
    </div>
  );
}
